import React, { useState, useEffect } from 'react';
import newsletterService from '../services/newsletterService';
import { Newsletter } from '../types/contentManagement';
import './NewsletterDisplay.css';

const NewsletterDisplay: React.FC = () => {
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedYear, setExpandedYear] = useState<number | null>(new Date().getFullYear());

  useEffect(() => {
    fetchNewsletters();
  }, []);

  const toggleYear = (year: number) => {
    setExpandedYear(prev => prev === year ? null : year);
  };

  const fetchNewsletters = async () => {
    try {
      setLoading(true);
      const data = await newsletterService.getVisibleNewsletters();
      setNewsletters(data);
      setError(null);
    } catch (err) {
      setError('Failed to load newsletters');
      console.error('Error fetching newsletters:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group newsletters by year
  const groupByYear = () => {
    const grouped: { [year: number]: Newsletter[] } = {};
    
    newsletters.forEach(newsletter => {
      if (!grouped[newsletter.year]) {
        grouped[newsletter.year] = [];
      }
      grouped[newsletter.year].push(newsletter);
    });

    // Sort years in descending order (current year first)
    const sortedYears = Object.keys(grouped)
      .map(Number)
      .sort((a, b) => b - a);

    return sortedYears.map(year => ({
      year,
      newsletters: grouped[year]
    }));
  };

  // Group newsletters by frequency within a year
  const groupByFrequency = (yearNewsletters: Newsletter[]) => {
    const yearly = yearNewsletters.filter(n => n.frequency === 'Yearly');
    const quarterly = yearNewsletters.filter(n => n.frequency === 'Quarterly');
    const monthly = yearNewsletters.filter(n => n.frequency === 'Monthly');

    // Sort quarterly by quarter
    const quarterOrder = ['Q1', 'Q2', 'Q3', 'Q4'];
    quarterly.sort((a, b) => {
      const aIndex = a.month ? quarterOrder.indexOf(a.month) : -1;
      const bIndex = b.month ? quarterOrder.indexOf(b.month) : -1;
      return bIndex - aIndex; // Descending order
    });

    // Sort monthly by month
    const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    monthly.sort((a, b) => {
      const aIndex = a.month ? monthOrder.indexOf(a.month) : -1;
      const bIndex = b.month ? monthOrder.indexOf(b.month) : -1;
      return bIndex - aIndex; // Descending order
    });

    return { yearly, quarterly, monthly };
  };

  const handleNewsletterClick = (newsletter: Newsletter) => {
    const url = newsletter.link || newsletterService.getFileUrl(newsletter.file_path || '');
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const getFrequencyIcon = (frequency: string) => {
    switch (frequency) {
      case 'Yearly':
        return '📅';
      case 'Quarterly':
        return '📊';
      case 'Monthly':
        return '📰';
      default:
        return '📄';
    }
  };

  if (loading) {
    return (
      <div className="newsletter-display-container">
        <div className="newsletter-loading">
          <div className="loading-spinner"></div>
          <p>Loading newsletters...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="newsletter-display-container">
        <div className="newsletter-error">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const yearGroups = groupByYear();

  if (yearGroups.length === 0) {
    return (
      <div className="newsletter-display-container">
        <div className="newsletter-empty-state">
          <div className="empty-icon">📰</div>
          <h3>No Newsletters Available</h3>
          <p>There are currently no newsletters to display. Check back later for updates!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="newsletter-display-container">
      <div className="newsletter-content">
        {yearGroups.map(({ year, newsletters: yearNewsletters }) => {
          const { yearly, quarterly, monthly } = groupByFrequency(yearNewsletters);
          const isCurrentYear = year === new Date().getFullYear();

          const isExpanded = expandedYear === year;

          return (
            <div key={year} className="newsletter-year-section">
              <div 
                className={`year-header ${isExpanded ? 'expanded' : ''}`}
                onClick={() => toggleYear(year)}
              >
                <h2 className="year-title">
                  <span className="year-number">{year}</span>
                  {isCurrentYear && <span className="current-badge">Current</span>}
                </h2>
                <span className="accordion-icon">{isExpanded ? '−' : '+'}</span>
              </div>
              <div className="year-divider"></div>

              {isExpanded && (
              <div className="year-content">
              {/* Yearly Newsletters */}
              {yearly.length > 0 && (
                <div className="frequency-group">
                  <h3 className="frequency-title">
                    <span className="frequency-icon">📅</span>
                    Yearly Newsletters
                  </h3>
                  <div className="newsletter-grid">
                    {yearly.map(newsletter => (
                      <div
                        key={newsletter.id}
                        className="newsletter-card"
                        onClick={() => handleNewsletterClick(newsletter)}
                      >
                        <div className="newsletter-card-header">
                          <span className="newsletter-type-icon">
                            {newsletter.file_path ? '📄' : '🔗'}
                          </span>
                          <span className="newsletter-frequency-badge yearly">
                            Yearly
                          </span>
                        </div>
                        <h4 className="newsletter-title">{newsletter.title}</h4>
                        <div className="newsletter-action">
                          <span className="action-text">
                            {newsletter.file_path ? 'View PDF' : 'Open Link'}
                          </span>
                          <span className="action-icon">→</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quarterly Newsletters */}
              {quarterly.length > 0 && (
                <div className="frequency-group">
                  <h3 className="frequency-title">
                    <span className="frequency-icon">📊</span>
                    Quarterly Newsletters
                  </h3>
                  <div className="newsletter-grid">
                    {quarterly.map(newsletter => (
                      <div
                        key={newsletter.id}
                        className="newsletter-card"
                        onClick={() => handleNewsletterClick(newsletter)}
                      >
                        <div className="newsletter-card-header">
                          <span className="newsletter-type-icon">
                            {newsletter.file_path ? '📄' : '🔗'}
                          </span>
                          <span className="newsletter-frequency-badge quarterly">
                            {newsletter.month}
                          </span>
                        </div>
                        <h4 className="newsletter-title">{newsletter.title}</h4>
                        <div className="newsletter-action">
                          <span className="action-text">
                            {newsletter.file_path ? 'View PDF' : 'Open Link'}
                          </span>
                          <span className="action-icon">→</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly Newsletters */}
              {monthly.length > 0 && (
                <div className="frequency-group">
                  <h3 className="frequency-title">
                    <span className="frequency-icon">📰</span>
                    Monthly Newsletters
                  </h3>
                  <div className="newsletter-grid">
                    {monthly.map(newsletter => (
                      <div
                        key={newsletter.id}
                        className="newsletter-card"
                        onClick={() => handleNewsletterClick(newsletter)}
                      >
                        <div className="newsletter-card-header">
                          <span className="newsletter-type-icon">
                            {newsletter.file_path ? '📄' : '🔗'}
                          </span>
                          <span className="newsletter-frequency-badge monthly">
                            {newsletter.month}
                          </span>
                        </div>
                        <h4 className="newsletter-title">{newsletter.title}</h4>
                        <div className="newsletter-action">
                          <span className="action-text">
                            {newsletter.file_path ? 'View PDF' : 'Open Link'}
                          </span>
                          <span className="action-icon">→</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NewsletterDisplay;
